import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Stack,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import { Plus, Trash2, Edit2, Building2, Users, FileText, Shield } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

function CompanyManager() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [newCompany, setNewCompany] = useState({ 
    name: '', 
    phone: '', 
    address: ''
  });
  
  const [editingCompany, setEditingCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/companies', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setCompanies(data);
        setError(null);
      } else {
        setError(data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur réseau');
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/companies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newCompany)
      });
      const data = await response.json();
      if (response.ok) {
        setOpen(false);
        setNewCompany({ name: '', phone: '', address: '' });
        fetchCompanies();
      } else {
        alert(data.error || 'Erreur lors de la création');
      }
    } catch (err) {
      alert('Erreur réseau');
    }
    setLoading(false);
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/companies/${editingCompany.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editingCompany)
      });
      if (response.ok) {
        setEditOpen(false);
        fetchCompanies();
      } else {
        const data = await response.json();
        alert(data.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      alert('Erreur réseau');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entreprise ? Cette action est irréversible.')) return;
    
    setDeletingId(id);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/companies/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchCompanies();
      } else {
        const data = await response.json();
        alert(data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      alert('Erreur réseau');
    }
    setDeletingId(null);
  };

  const openEdit = (c) => {
    setEditingCompany({ ...c });
    setEditOpen(true);
  };

  const viewCompanyUsers = (companyId) => {
    navigate(`/admin/clients?company=${companyId}`);
  };

  // Statistiques
  const totalCompanies = companies.length;
  const internalCompanies = companies.filter(c => c.isInternal).length;
  const clientCompanies = totalCompanies - internalCompanies;
  const totalUsers = companies.reduce((acc, c) => acc + c.userCount, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Gestion des Entreprises</Typography>
        <Button 
          variant="contained" 
          startIcon={<Plus size={18} />}
          onClick={() => setOpen(true)}
        >
          Nouvelle Entreprise
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3, mb: 4 }}>
        <Card sx={{ bgcolor: 'background.paper', backgroundImage: 'none' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: 'rgba(0, 212, 170, 0.1)',
                display: 'flex'
              }}>
                <Building2 size={24} color="#00d4aa" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{totalCompanies}</Typography>
                <Typography variant="body2" color="text.secondary">Total entreprises</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: 'background.paper', backgroundImage: 'none' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: 'rgba(99, 102, 241, 0.1)',
                display: 'flex'
              }}>
                <Shield size={24} color="#6366f1" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{internalCompanies}</Typography>
                <Typography variant="body2" color="text.secondary">Interne (Global TI)</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: 'background.paper', backgroundImage: 'none' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: 'rgba(245, 158, 11, 0.1)',
                display: 'flex'
              }}>
                <FileText size={24} color="#f59e0b" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{clientCompanies}</Typography>
                <Typography variant="body2" color="text.secondary">Entreprises clientes</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: 'background.paper', backgroundImage: 'none' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                display: 'flex'
              }}>
                <Users size={24} color="#ef4444" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{totalUsers}</Typography>
                <Typography variant="body2" color="text.secondary">Utilisateurs total</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper', backgroundImage: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Entreprise</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell align="center">Utilisateurs</TableCell>
              <TableCell align="center">Projets</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1, 
                      bgcolor: c.isInternal ? 'rgba(99, 102, 241, 0.1)' : 'rgba(0, 212, 170, 0.1)',
                      display: 'flex'
                    }}>
                      <Building2 size={18} color={c.isInternal ? '#6366f1' : '#00d4aa'} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                      {c.address && (
                        <Typography variant="caption" color="text.secondary">{c.address}</Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {c.phone || 'Non renseigné'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={c.userCount} 
                    size="small" 
                    onClick={() => viewCompanyUsers(c.id)}
                    sx={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip label={c.submissionCount} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={c.isInternal ? 'Interne' : 'Client'} 
                    size="small" 
                    color={c.isInternal ? 'secondary' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton 
                      size="small" 
                      onClick={() => openEdit(c)} 
                      color="primary"
                      disabled={c.isInternal && !isSuperAdmin}
                    >
                      <Edit2 size={18} />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => handleDelete(c.id)} 
                      disabled={c.isInternal || c.userCount > 0 || c.submissionCount > 0 || deletingId === c.id}
                      color="error"
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Nouvelle Entreprise Cliente</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField 
              label="Nom de l'entreprise" 
              fullWidth 
              size="small"
              required
              value={newCompany.name}
              onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
            />
            
            <TextField 
              label="Téléphone" 
              fullWidth 
              size="small"
              value={newCompany.phone}
              onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
            />

            <TextField 
              label="Adresse" 
              fullWidth 
              size="small"
              multiline
              rows={2}
              value={newCompany.address}
              onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleCreate} 
            disabled={!newCompany.name || loading}
          >
            {loading ? 'Création...' : 'Créer l\'entreprise'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Modifier l'entreprise</DialogTitle>
        <DialogContent>
          {editingCompany && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField 
                label="Nom de l'entreprise" 
                fullWidth 
                size="small"
                required
                value={editingCompany.name}
                onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                disabled={editingCompany.isInternal}
              />
              
              <TextField 
                label="Téléphone" 
                fullWidth 
                size="small"
                value={editingCompany.phone || ''}
                onChange={(e) => setEditingCompany({ ...editingCompany, phone: e.target.value })}
              />

              <TextField 
                label="Adresse" 
                fullWidth 
                size="small"
                multiline
                rows={2}
                value={editingCompany.address || ''}
                onChange={(e) => setEditingCompany({ ...editingCompany, address: e.target.value })}
              />

              {editingCompany.isInternal && (
                <Alert severity="info">
                  L'entreprise interne (Global TI) ne peut pas être renommée.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleUpdate}
            disabled={loading || !editingCompany?.name}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CompanyManager;
